'use client';

import { useTranslations } from 'next-intl';
import { Package } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import type { Box } from '@/features/packing';

interface BoxSelectionCardProps {
  box: Box;
  selected: boolean;
  onToggle: (id: number) => void;
  disabled?: boolean;
}

export function BoxSelectionCard({
  box,
  selected,
  onToggle,
  disabled
}: BoxSelectionCardProps) {
  const t = useTranslations('shipping');

  const isReady =
    box.status === 'available' || box.status === 'ready_for_shipping';
  const isDisabled = disabled || !isReady;

  const statusLabel = isReady ? t('boxReady') : t('boxUnderInspection');
  const statusStyle = isReady
    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';

  return (
    <Card
      role='button'
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      aria-selected={selected}
      onClick={() => !isDisabled && onToggle(box.id)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
          e.preventDefault();
          onToggle(box.id);
        }
      }}
      className={cn(
        'relative transition-all',
        !isDisabled && 'cursor-pointer hover:shadow-md',
        isDisabled && 'cursor-not-allowed opacity-50',
        selected && 'bg-primary/5 ring-primary ring-2'
      )}
    >
      <CardContent className='p-4'>
        {/* Top row: checkbox + icon */}
        <div className='flex items-start justify-between'>
          <Checkbox
            checked={selected}
            disabled={isDisabled}
            onCheckedChange={() => !isDisabled && onToggle(box.id)}
            onClick={(e) => e.stopPropagation()}
            aria-label={t('selectBox', { number: box.box_number })}
          />
          <Package className='text-muted-foreground h-5 w-5' />
        </div>

        {/* Box number */}
        <div className='mt-3 text-center'>
          <p className='text-lg font-bold tracking-wide'>{box.box_number}</p>
          {box.box_type?.name && (
            <p className='text-muted-foreground mt-0.5 text-xs'>
              {box.box_type.name}
            </p>
          )}
        </div>

        {/* Bottom row: count + status */}
        <div className='mt-3 flex items-center justify-between'>
          {box.registrations_count != null && (
            <Badge variant='outline' className='text-xs'>
              {box.registrations_count} {t('container')}
            </Badge>
          )}
          <Badge variant='secondary' className={cn('text-xs', statusStyle)}>
            {statusLabel}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
